import 'amazon-connect-chatjs';
import { Language, Subsidiary } from '@coolblue-development/constants';
import cancelLiveChatTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/cancelLiveChatTrigger';
import endLiveChatTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/endLiveChatTrigger';
import showErrorMessageTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/showErrorMessageTrigger';
import startLiveChatTrigger from '@coolblue-development/ga4-events/triggers/Chatbot/startLiveChatTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import { formatTime } from '@coolblue-development/next-formatting-utils/core';
import { useEffect, useRef, useState } from 'react';
import { agentNameStorageKey, chatMessagesStorageKey, previousContactIdStorageKey, startChatDetailsStorageKey, timestampStorageKey, } from '../../../constants/liveChat.js';
import { LiveChatHistoryService } from '../../../domain/live-chat/services/liveChatHistoryService.js';
import { isLocalStorageAvailable } from '../../../utils/isLocalStorageAvailable.js';
const chatMessageStorageLimit = 30;
const agentTypingIndicatorTimeout = 10000;
const defaultConversationSummary = 'Something went wrong. The summary from the chatbot conversation is not available.';
const shouldUseLocalStorage = isLocalStorageAvailable();
const getIdsFromStorage = () => {
    if (shouldUseLocalStorage) {
        const startChatDetails = localStorage.getItem(startChatDetailsStorageKey);
        if (startChatDetails) {
            try {
                const { chatId, contactId, participantId, participantToken } = JSON.parse(startChatDetails);
                return { chatId, contactId, participantId, participantToken };
            }
            catch (_a) {
                return undefined;
            }
        }
    }
    return undefined;
};
const storeMessage = (message) => {
    if (!shouldUseLocalStorage) {
        return;
    }
    const storedMessages = localStorage.getItem(chatMessagesStorageKey);
    // TODO: Once the webshop is completely migrated, we can move this message type over to the new one
    const parsedStoredMessages = storedMessages
        ? JSON.parse(storedMessages)
        : [];
    parsedStoredMessages.push({
        Content: message.text || '',
        ParticipantRole: message.isUser ? 'CUSTOMER' : 'AGENT',
        time: message.timestamp,
    });
    if (parsedStoredMessages.length > chatMessageStorageLimit) {
        parsedStoredMessages.shift();
    }
    localStorage.setItem(chatMessagesStorageKey, JSON.stringify(parsedStoredMessages));
};
const clearActiveChatStorage = () => {
    if (!shouldUseLocalStorage) {
        return;
    }
    localStorage.removeItem(startChatDetailsStorageKey);
    localStorage.removeItem(chatMessagesStorageKey);
    localStorage.removeItem(timestampStorageKey);
};
const getChatMessageFromEvent = (absoluteTime, participantRole, content, attachment) => {
    const dateTime = new Date(absoluteTime);
    return {
        isUser: participantRole === 'CUSTOMER',
        timestamp: formatTime({
            date: dateTime,
            language: Language.ENGLISH,
            subsidiary: Subsidiary.NL,
        }),
        text: content,
        attachment,
    };
};
export const useLiveChat = ({ fetchStartChatDetailsUrl, onMessage }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const [agentName, setAgentName] = useState();
    const [hasAgentLeft, setHasAgentLeft] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [hasCanceled, setHasCanceled] = useState(false);
    const [connectSession, setConnectSession] = useState(null);
    const [history, setHistory] = useState(LiveChatHistoryService.getAll());
    const typingTimeoutRef = useRef(null);
    const messagesRef = useRef(messages);
    const hasRetriedConnectionRef = useRef(false);
    const isActive = hasStarted && !hasEnded;
    const { trackEvent } = useAnalytics();
    const logger = useClientLogger();
    useEffect(() => {
        if (connectSession && hasCanceled) {
            connectSession.disconnectParticipant();
        }
    }, [connectSession, hasCanceled]);
    useEffect(() => {
        /**
         * This needs to be done because the onEnd callback would be registered once with an empty messages state.
         * The active live chat is then migrated with an empty array, hence we need a ref instead.
         **/
        messagesRef.current = messages;
    }, [messages]);
    const endChat = () => {
        clearActiveChatStorage();
        if (shouldUseLocalStorage) {
            // TODO: Removing this until we properly handle live chat history
            localStorage.removeItem(agentNameStorageKey);
        }
        setIsLoading(false);
        setHasEnded(true);
    };
    const startChat = async (resume = false, chatbotConversationSummary = defaultConversationSummary, intentInteractionId, estimatedQueueWaitingTime, retryConnection) => {
        if (retryConnection) {
            hasRetriedConnectionRef.current = true;
            logger.info('[SS] Client - Retrying connection after failure');
        }
        let startChatStorage;
        let localStorageStartChatDetails = null;
        let localStoragePreviousContactId = null;
        // 1. If it detects that LocalStorage is enabled, then it tries to get ChatDetails and Previous Contact ID
        if (shouldUseLocalStorage) {
            localStorageStartChatDetails = localStorage.getItem(startChatDetailsStorageKey);
            localStoragePreviousContactId = localStorage.getItem(previousContactIdStorageKey);
        }
        // 2. If resume attribute is true AND there are no ChatDetails in the Local Storage. Then stop
        if (resume && !localStorageStartChatDetails) {
            return;
        }
        // 3. If there is LocalStorage then get's the timestamp of the Chat otherwise it sets a new date
        if (shouldUseLocalStorage) {
            const localStorageTimestamp = localStorage.getItem(timestampStorageKey);
            if (!localStorageTimestamp) {
                localStorage.setItem(timestampStorageKey, String(Date.now()));
            }
        }
        // 4. Show flag is loading
        setIsLoading(true);
        try {
            // 5. If there are ChatDetails in LS, then save it for later and log
            if (localStorageStartChatDetails) {
                startChatStorage = JSON.parse(localStorageStartChatDetails);
                logger.info('[SS] Client - Resuming live chat', {
                    chatId: startChatStorage === null || startChatStorage === void 0 ? void 0 : startChatStorage.chatId,
                    contactId: startChatStorage === null || startChatStorage === void 0 ? void 0 : startChatStorage.contactId,
                });
                // 6. If there are no ChatDetails in LS and there is a Fetch Chat Details URL,
                // then do a Fetch with Summary and Previous Contact Id
            }
            else if (fetchStartChatDetailsUrl) {
                const response = await fetch(fetchStartChatDetailsUrl, {
                    method: 'POST',
                    body: JSON.stringify({
                        chatbotConversationSummary,
                        intentInteractionId: intentInteractionId,
                        previousContactId: localStoragePreviousContactId || undefined,
                        estimatedQueueWaitingTime,
                    }),
                });
                // 7. Response not ok, throw
                if (!response.ok) {
                    throw new Error('Fetch live chat details response not ok');
                }
                // 8. Try to parse the JSON response and save ChatDetails + timestamp
                const startChatResult = await response.json();
                const timestamp = Date.now();
                startChatStorage = { ...startChatResult, timestamp };
                // 9. If LS, save the ChatDetails + timestamp
                if (shouldUseLocalStorage) {
                    localStorage.setItem(startChatDetailsStorageKey, JSON.stringify(startChatStorage));
                    logger.info('[SS] Client - Starting new live chat', {
                        chatId: startChatStorage.chatId,
                        contactId: startChatStorage.contactId,
                    });
                }
            }
            // 10. If no ChatDetails + timestamp, throw
            if (!startChatStorage) {
                throw new Error('No start chat storage');
            }
            // 11. Config the ChatSession
            connect.ChatSession.setGlobalConfig({ region: 'eu-central-1' });
            // 12. Create the connection with ChatDetails
            const chatConnectSession = connect.ChatSession.create({
                chatDetails: startChatStorage,
                type: 'CUSTOMER',
            });
            // 13. Connect
            const { connectSuccess } = await chatConnectSession.connect();
            // 14. If connection is not success, then throws
            if (!connectSuccess) {
                throw new Error('Connect session failed');
            }
            // 15. Has started flag = true
            setHasStarted(true);
            // 16. If LS, save the ContactID in LS
            if (shouldUseLocalStorage) {
                localStorage.setItem(previousContactIdStorageKey, startChatStorage.contactId);
            }
            // 17. If is not resuming, then log that a new live chat has started
            if (!localStorageStartChatDetails) {
                logger.info('[SS] Client - New live chat started', {
                    chatId: startChatStorage.chatId,
                    contactId: startChatStorage.contactId,
                });
            }
            // 18. Save connection in variable
            setConnectSession(chatConnectSession);
            // 19. Get the CS Agent name from LS
            let localStorageAgentName = null;
            if (shouldUseLocalStorage) {
                localStorageAgentName = localStorage.getItem(agentNameStorageKey);
            }
            // 20. If CS Agent Name, save it in variable, and set is loading false
            if (localStorageAgentName) {
                setAgentName(localStorageAgentName);
                setIsLoading(false);
                logger.info('[SS] Client - Live chat agent name retrieved from local storage', {
                    chatId: startChatStorage.chatId,
                    contactId: startChatStorage.contactId,
                    agentName: localStorageAgentName,
                });
            }
            chatConnectSession.onConnectionEstablished(async () => {
                var _a;
                const transcript = await chatConnectSession.getTranscript({});
                if ((_a = transcript.data) === null || _a === void 0 ? void 0 : _a.Transcript) {
                    const transcriptMessages = transcript.data.Transcript.filter(({ Type }) => Type === 'MESSAGE').map(transcriptItem => {
                        const { AbsoluteTime, ParticipantRole, Content } = transcriptItem;
                        return getChatMessageFromEvent(AbsoluteTime, ParticipantRole, Content);
                    });
                    if (transcriptMessages.length > 0) {
                        setMessages([...transcriptMessages]);
                    }
                }
            });
            chatConnectSession.onMessage(async (messageEvent) => {
                const { Content, ParticipantRole, Attachments, AbsoluteTime, DisplayName, ContentType, Id, Type, } = messageEvent.data;
                const ids = getIdsFromStorage();
                logger.info('[SS] Client - Live chat message event', {
                    chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                    contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
                    eventType: Type,
                    messageId: Id,
                    participantRole: ParticipantRole,
                });
                // Check when connected to a CS agent
                if (!localStorageAgentName &&
                    ParticipantRole === 'AGENT' &&
                    DisplayName &&
                    ContentType === 'application/vnd.amazonaws.connect.event.participant.joined') {
                    setAgentName(DisplayName);
                    setIsLoading(false);
                    if (shouldUseLocalStorage) {
                        localStorage.setItem(agentNameStorageKey, DisplayName);
                    }
                    logger.info('[SS] Client - Live chat agent name message received', {
                        chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                        agentName: DisplayName,
                    });
                    trackEvent(startLiveChatTrigger());
                }
                setIsAgentTyping(false);
                // TODO: Implement attachments
                let attachment;
                if (Attachments) {
                    try {
                        attachment = await chatConnectSession.downloadAttachment({
                            attachmentId: Attachments[0].AttachmentId,
                        });
                    }
                    catch (_a) {
                        attachment = undefined;
                    }
                }
                if (Content || attachment) {
                    const chatMessage = getChatMessageFromEvent(AbsoluteTime, ParticipantRole, Content, attachment);
                    setMessages(prevMessages => [...prevMessages, chatMessage]);
                    storeMessage(chatMessage);
                    logger.info('[SS] Client - Live chat message rendered and stored', {
                        chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                        messageId: Id,
                        participantRole: ParticipantRole,
                        eventType: Type,
                    });
                    if (onMessage) {
                        onMessage(chatMessage);
                    }
                }
            });
            const handleAgentTyping = () => {
                setIsAgentTyping(true);
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
                typingTimeoutRef.current = setTimeout(() => {
                    setIsAgentTyping(false);
                }, agentTypingIndicatorTimeout);
            };
            chatConnectSession.onTyping(event => {
                const { ParticipantRole } = event.data;
                if (ParticipantRole === 'AGENT') {
                    handleAgentTyping();
                }
            });
            chatConnectSession.onEnded(() => {
                setHasAgentLeft(true);
                LiveChatHistoryService.migrateActiveChatToHistory(messagesRef.current);
                setHistory(LiveChatHistoryService.getAll());
                const ids = getIdsFromStorage();
                logger.info('[SS] Client - Live chat end event received', {
                    chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                    contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
                });
                trackEvent(endLiveChatTrigger());
                endChat();
            });
        }
        catch (error) {
            const ids = getIdsFromStorage();
            logger.error('[SS] Client - Live chat error', error, {
                chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
                participantId: ids === null || ids === void 0 ? void 0 : ids.participantId,
                participantToken: ids === null || ids === void 0 ? void 0 : ids.participantToken,
            });
            clearActiveChatStorage();
            if (shouldUseLocalStorage) {
                localStorage.removeItem(agentNameStorageKey);
            }
            setIsLoading(false);
            if (!hasRetriedConnectionRef.current) {
                startChat(resume, chatbotConversationSummary, intentInteractionId, estimatedQueueWaitingTime, true);
            }
        }
    };
    const sendMessage = async (text) => {
        if (connectSession) {
            try {
                await connectSession.sendMessage({ contentType: 'text/plain', message: text });
                const ids = getIdsFromStorage();
                logger.info('[SS] Client - Live chat message sent', {
                    chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                    contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
                });
            }
            catch (error) {
                const chatErroredMessage = {
                    isUser: true,
                    timestamp: formatTime({
                        date: new Date(),
                        language: Language.ENGLISH,
                        subsidiary: Subsidiary.NL,
                    }),
                    text,
                    isError: true,
                };
                setMessages(prevMessages => [...prevMessages, chatErroredMessage]);
                trackEvent(showErrorMessageTrigger({ content: 'Oeps, er ging iets mis' }));
                const ids = getIdsFromStorage();
                logger.error('[SS] Client - Live chat send message error', error, {
                    chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
                    contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
                });
            }
        }
    };
    const sendTypingEvent = () => {
        if (connectSession) {
            connectSession.sendEvent({
                contentType: 'application/vnd.amazonaws.connect.event.typing',
            });
        }
    };
    const reset = () => {
        setIsLoading(false);
        setMessages([]);
        setIsAgentTyping(false);
        setAgentName(undefined);
        setHasAgentLeft(false);
        setHasStarted(false);
        setHasEnded(false);
        setConnectSession(null);
        setHasCanceled(false);
        typingTimeoutRef.current = null;
    };
    const cancelConnection = () => {
        setHasCanceled(true);
        trackEvent(cancelLiveChatTrigger());
        const ids = getIdsFromStorage();
        logger.info('[SS] Client - Live chat canceled', {
            chatId: ids === null || ids === void 0 ? void 0 : ids.chatId,
            contactId: ids === null || ids === void 0 ? void 0 : ids.contactId,
        });
        endChat();
    };
    return {
        agentName,
        hasAgentLeft,
        hasCanceled,
        hasEnded,
        hasStarted,
        history,
        isActive,
        isAgentTyping,
        isLoading,
        messages,
        cancelConnection,
        reset,
        sendMessage,
        sendTypingEvent,
        startChat,
    };
};
//# sourceMappingURL=useLiveChat.js.map